import { Box } from "@chakra-ui/react"
import { useEffect, useMemo, useState } from "react"
import HTMLText from "../text/HTMLText"
import RichTextEditorControls from "./RichTextEditor.controls"
import RichTextEditorEditMode from "./RichTextEditor.edit-mode"
import type {
  RichTextEditorControlBarProps,
  RichTextEditorControlBarResolved,
  RichTextEditorPermissionMode,
  RichTextEditorProps
} from "./types"

function resolveRichTextEditorControlBar(
  permissionMode: RichTextEditorPermissionMode,
  controlBar?: RichTextEditorControlBarProps
): RichTextEditorControlBarResolved {
  const editable = permissionMode !== "view-only"
  const c = controlBar ?? {}
  return {
    showEditButton: c.showEditButton ?? editable,
    showSaveCancelButtons: c.showSaveCancelButtons ?? editable,
    editButtonLabel: c.editButtonLabel ?? "Edit",
    saveButtonLabel: c.saveButtonLabel ?? "Save",
    cancelButtonLabel: c.cancelButtonLabel ?? "Cancel",
    saveButtonDisabled: c.saveButtonDisabled ?? false,
    cancelButtonDisabled: c.cancelButtonDisabled ?? false,
    onEdit: c.onEdit,
    onSave: c.onSave,
    onCancel: c.onCancel
  }
}

const RichTextEditor = (props: RichTextEditorProps) => {
  const {
    value,
    onChange,
    mode: modeProp = "view",
    permissionMode = "editable",
    controlBar,
    editorStyles,
    editor,
    enableSlashMenu = false,
    autoFocus = false,
    disabled,
    renderUploadImage,
    renderUploadFile
  } = props

  const canEdit = permissionMode !== "view-only"
  const [mode, setMode] = useState<"view" | "edit">(canEdit ? modeProp : "view")

  const resolvedControlBar = useMemo(
    () => resolveRichTextEditorControlBar(permissionMode, controlBar),
    [permissionMode, controlBar]
  )

  useEffect(() => {
    setMode(canEdit ? modeProp : "view")
  }, [modeProp, canEdit])

  if (!canEdit) {
    return <HTMLText html={value} />
  }

  if (mode === "view") {
    if (disabled) {
      return <HTMLText html={value} />
    }

    return (
      <Box pos="relative">
        <RichTextEditorControls
          mode={mode}
          canEdit={canEdit}
          setMode={setMode}
          controlBar={resolvedControlBar}
        />
        <HTMLText html={value} />
      </Box>
    )
  }

  return (
    <Box pos="relative">
      <RichTextEditorEditMode
        value={value}
        onChange={onChange}
        controls={
          <RichTextEditorControls
            mode={mode}
            canEdit={canEdit}
            setMode={setMode}
            controlBar={resolvedControlBar}
          />
        }
        editor={editor}
        editorStyles={editorStyles}
        enableSlashMenu={enableSlashMenu}
        autoFocus={autoFocus}
        disabled={disabled}
        renderUploadImage={renderUploadImage}
        renderUploadFile={renderUploadFile}
      />
    </Box>
  )
}

export default RichTextEditor
